import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FlowjoCommons } from '@portal/portal-core';
import { updateUserField, setUserPhantom, setUserEmailVerified } from 'ducks/super-client/actions';
import { selectUserFieldUpdateError } from 'selectors/super-admin';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { FjInput } from 'components/primitives';
import { getCountryOptions } from 'utils';

const { Regional } = FlowjoCommons;

interface CountryOption {
  label: string;
  value: string;
}

interface UserFields {
  id: string;
  primaryEmail: string;
  firstname?: string;
  lastname?: string;
  countryCode?: string;
  country?: string;
  isPhantom?: boolean;
  verified?: boolean;
  [key: string]: unknown;
}

interface UserFieldsEditorProps {
  user: UserFields;
  onUserUpdate?: (user: UserFields) => void;
}

interface ToggleProps {
  on: number;
}

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const FormSection = styled.div`
  width: 100%;
  margin: 10px 0;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
`;

const CountrySelect = styled.select`
  width: 100%;
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  height: 35px;

  &:focus {
    outline: none;
    border-color: #156777;
    box-shadow: 0 0 0 2px rgba(21, 103, 119, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 15px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  background-color: #156777;
  color: white;
  border: 1px solid #156777;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #124f5d;
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  background-color: white;
  color: #156777;
  border: 1px solid #156777;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const ToggleTrack = styled.button<ToggleProps>`
  position: relative;
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${({ on }) => (on ? '#156777' : '#ccc')};
  flex-shrink: 0;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(21, 103, 119, 0.3);
  }
`;

const ToggleThumb = styled.span<ToggleProps>`
  position: absolute;
  top: 3px;
  left: ${({ on }) => (on ? '25px' : '3px')};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ToggleStatus = styled.span<ToggleProps>`
  font-size: 13px;
  color: ${({ on }) => (on ? '#156777' : '#999')};
`;

const InfoMessage = styled.div`
  padding: 10px;
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const ViewValue = styled.div`
  padding: 8px;
  font-size: 14px;
  color: #333;
  background-color: #f9f9f9;
  border-radius: 4px;
  min-height: 35px;
  display: flex;
  align-items: center;
`;

const UserFieldsEditor = ({ user, onUserUpdate }: UserFieldsEditorProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form fields
  const [firstname, setFirstname] = useState(user.firstname || '');
  const [lastname, setLastname] = useState(user.lastname || '');
  const [countryCode, setCountryCode] = useState(user.countryCode || user.country || '');

  // Memoized — computed once, not rebuilt on every render; also filters embargoed countries
  const countryOptions = useMemo<CountryOption[]>(() => getCountryOptions(), []);

  // Track whether a save is in-flight so the error effect only fires after a real dispatch
  const isSavingRef = useRef(false);
  // `selectUserFieldUpdateError` returns the memoized error value (typed as unknown);
  // it holds an error message string or null.
  const reduxError = useAppSelector(selectUserFieldUpdateError) as string | null;

  useEffect(() => {
    if (!isSavingRef.current) return;
    if (!reduxError) return;
    // Saga reported an error — surface it and keep the form open for retry
    isSavingRef.current = false;
    setIsSaving(false);
    setSaveError(reduxError);
  }, [reduxError]);

  const handleEditToggle = (): void => {
    setIsEditing(!isEditing);
    setSaveError(null);
    // Reset form fields to current user values
    if (!isEditing) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
      setCountryCode(user.countryCode || user.country || '');
    }
  };

  const handleSaveProfile = (): void => {
    if (isSaving) return;

    // Build an ordered list of changed fields: firstname → lastname → countryCode
    const pendingUpdates: [string, string][] = [];
    if (firstname !== user.firstname) pendingUpdates.push(['firstname', firstname]);
    if (lastname !== user.lastname) pendingUpdates.push(['lastname', lastname]);
    if (countryCode !== (user.countryCode || user.country || ''))
      pendingUpdates.push(['countryCode', countryCode]);

    if (pendingUpdates.length === 0) {
      setIsEditing(false);
      return;
    }

    setSaveError(null);
    setIsSaving(true);
    isSavingRef.current = true;

    // Optimistically update the local user object
    if (onUserUpdate) {
      const updates = Object.fromEntries(pendingUpdates);
      onUserUpdate({ ...user, ...updates });
    }

    // Dispatch updates sequentially with a 400ms gap so each request is
    // processed before the next is sent.
    const DISPATCH_INTERVAL = 400;
    pendingUpdates.forEach(([field, value], index) => {
      setTimeout(() => {
        dispatch(updateUserField(user.primaryEmail, field, value));
      }, index * DISPATCH_INTERVAL);
    });

    // Close the form after all dispatches have fired plus a 1s buffer for the
    // last request to complete. The error effect will interrupt this if any
    // saga reports a failure.
    const closeDelay = pendingUpdates.length * DISPATCH_INTERVAL + 1000;
    setTimeout(() => {
      if (!isSavingRef.current) return;
      isSavingRef.current = false;
      setIsSaving(false);
      setIsEditing(false);
    }, closeDelay);
  };

  const handlePhantomToggle = (): void => {
    const newPhantomState = !user.isPhantom;
    dispatch(setUserPhantom(user.id, newPhantomState));

    if (onUserUpdate) {
      onUserUpdate({ ...user, isPhantom: newPhantomState });
    }
  };

  const handleVerifiedToggle = (): void => {
    const newVerified = !user.verified;
    dispatch(setUserEmailVerified(user.primaryEmail, newVerified));

    if (onUserUpdate) {
      onUserUpdate({ ...user, verified: newVerified });
    }
  };

  const countryDisplay =
    user.countryCode || user.country
      ? `${Regional.getCountryName(user.countryCode || user.country)} (${
        user.countryCode || user.country
      })`
      : 'Not set';

  // Render edit mode
  if (isEditing) {
    return (
      <EditorContainer>
        <FormSection>
          <Label>First Name</Label>
          <FjInput
            value={firstname}
            onChange={(value: string) => setFirstname(value)}
            placeholder="First Name"
            height={35}
          />
        </FormSection>

        <FormSection>
          <Label>Last Name</Label>
          <FjInput
            value={lastname}
            onChange={(value: string) => setLastname(value)}
            placeholder="Last Name"
            height={35}
          />
        </FormSection>

        <FormSection>
          <Label>Country</Label>
          <CountrySelect
            value={countryCode}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCountryCode(e.target.value)}
          >
            <option value="">Select a country</option>
            {countryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </CountrySelect>
        </FormSection>

        {saveError && <ErrorMessage>{saveError}</ErrorMessage>}

        <ButtonGroup>
          <ActionButton onClick={handleSaveProfile} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </ActionButton>
          <SecondaryButton onClick={handleEditToggle} disabled={isSaving}>
            Cancel
          </SecondaryButton>
        </ButtonGroup>
      </EditorContainer>
    );
  }

  // Render view mode
  return (
    <EditorContainer>
      <InfoMessage>
        Click &ldquo;Edit Profile&rdquo; to update user information. Use &ldquo;Convert to Real
        User&rdquo; to convert a phantom user (sets phantom: false) after populating their profile.
      </InfoMessage>

      <FormSection>
        <Label>First Name</Label>
        <ViewValue>{user.firstname || <em style={{ color: '#999' }}>Not set</em>}</ViewValue>
      </FormSection>

      <FormSection>
        <Label>Last Name</Label>
        <ViewValue>{user.lastname || <em style={{ color: '#999' }}>Not set</em>}</ViewValue>
      </FormSection>

      <FormSection>
        <Label>Country</Label>
        <ViewValue>{countryDisplay}</ViewValue>
      </FormSection>

      <ButtonGroup>
        <ActionButton onClick={handleEditToggle}>Edit Profile</ActionButton>
        <ToggleRow>
          <ToggleLabel>Phantom User</ToggleLabel>
          <ToggleTrack
            on={user.isPhantom ? 1 : 0}
            onClick={handlePhantomToggle}
            aria-label="Toggle phantom user"
          >
            <ToggleThumb on={user.isPhantom ? 1 : 0} />
          </ToggleTrack>
          <ToggleStatus on={user.isPhantom ? 1 : 0}>
            {user.isPhantom ? 'True' : 'False'}
          </ToggleStatus>
        </ToggleRow>
        <ToggleRow>
          <ToggleLabel>Email Verified</ToggleLabel>
          <ToggleTrack
            on={user.verified ? 1 : 0}
            onClick={handleVerifiedToggle}
            aria-label="Toggle email verified"
          >
            <ToggleThumb on={user.verified ? 1 : 0} />
          </ToggleTrack>
          <ToggleStatus on={user.verified ? 1 : 0}>{user.verified ? 'True' : 'False'}</ToggleStatus>
        </ToggleRow>
      </ButtonGroup>
    </EditorContainer>
  );
};

export default UserFieldsEditor;
